import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import Header from '../common/Header';
import HeroCollage from './HeroCollage';
import HeroImage from './HeroImage';

const LandingPage = ({ currentPage, onNavigate, isInitialLoad = true, isLoadingComplete = true }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHeroLoaded, setIsHeroLoaded] = useState(false);

  const { isDark, colors } = useTheme();

  const getInitialState = (defaultInitial) => {
    if (!isLoadingComplete) {
      return { opacity: 0, y: 100 };
    }
    return defaultInitial;
  };

  const getAnimateState = (defaultAnimate) => {
    if (!isLoadingComplete) {
      return { opacity: 0, y: 100 };
    }
    return defaultAnimate;
  };

  return (
    <div 
      className="w-screen min-h-screen overflow-x-hidden relative transition-colors duration-500"
      style={{ 
        backgroundColor: colors.primary,
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif'
      }}
    >
      {/* Header */}
      <Header 
        currentPage={currentPage}
        onNavigate={onNavigate}
        showDescription={false}
        showNavButtons={false}
        isLandingPage={true}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        isHeroLoaded={isHeroLoaded}
      />

      {/* Hero Collage Section */}
      <HeroCollage 
        isLoadingComplete={isLoadingComplete}
        onHeroLoaded={() => setIsHeroLoaded(true)}
      />

      {/* Title + Subtitle Section */}
      <section 
        className="w-full px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12 relative z-10 transition-colors duration-500"
        style={{ backgroundColor: colors.primary }}
      >
        <div className="w-full flex flex-col lg:flex-row lg:justify-between lg:items-end gap-4 sm:gap-6 lg:gap-8 mb-6 sm:mb-8 md:mb-10">
          {/* Content can be added here if needed */}
        </div>
      </section>

      {/* New Hero Image Component */}
      <HeroImage 
        isInitialLoad={isInitialLoad}
        isLoadingComplete={isLoadingComplete}
        getInitialState={getInitialState}
        getAnimateState={getAnimateState}
        isDark={isDark}
        colors={colors}
      />

      {/* Selected Works Section */}
      <section 
        className="flex flex-col items-center justify-center min-h-screen py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12 relative z-10 transition-colors duration-500"
        style={{ backgroundColor: colors.primary }}
      >
        {/* Section Header */}
        <div className="flex flex-col items-start w-full max-w-[1200px] mb-12 sm:mb-16 md:mb-20 lg:mb-24">
          {/* Divider line */}
          <motion.div 
            className="w-full h-px mb-6 sm:mb-8 md:mb-10 lg:mb-12"
            initial={getInitialState({ scaleX: 0 })}
            whileInView={getAnimateState({ scaleX: 1 })}
            transition={{ 
              duration: 1.2, 
              ease: [0.16, 1, 0.3, 1] 
            }}
            viewport={{ once: true, margin: '-100px' }}
            style={{ transformOrigin: 'left' }}
          >
            <div 
              className="w-full h-px"
              style={{
                background: isDark 
                  ? 'linear-gradient(to right, #333333, #1a1a1a, transparent)' 
                  : 'linear-gradient(to right, #d4d1c9, #eae7de, transparent)'
              }}
            />
          </motion.div>

          {/* Title */}
          <div className="flex flex-col gap-1 sm:gap-2 mb-2 sm:mb-3 md:mb-4">
            <motion.div
              initial={getInitialState({ opacity: 0, y: 40 })}
              whileInView={getAnimateState({ opacity: 1, y: 0 })}
              transition={{ 
                duration: 1.2, 
                ease: [0.16, 1, 0.3, 1] 
              }}
              viewport={{ once: true, margin: '-100px' }}
              className="font-normal leading-[1] transition-colors duration-500"
              style={{
                fontSize: 'clamp(32px, 8.5vw, 60px)',
                fontWeight: 400,
                letterSpacing: '-0.03em',
                color: colors.text.primary
              }}
            >
              SELECTED WORKS
            </motion.div>
          </div>
        </div>
       
        {/* Projects Grid */}
        <div className="w-full max-w-[1200px] space-y-10 sm:space-y-12 md:space-y-16 lg:space-y-20">
          {/* First Project Row */}
          <div className="flex flex-col xl:flex-row gap-6 sm:gap-8 md:gap-10 lg:gap-12 xl:gap-6">
            {/* Large Project - The Ocean */}
            <div className="flex-[2_0_0] space-y-4 sm:space-y-6">
              <motion.div
                className="group relative cursor-pointer"
                initial={getInitialState({ opacity: 0, y: 140 })}
                whileInView={getAnimateState({ opacity: 1, y: 0 })}
                transition={{ 
                  duration: 2, 
                  ease: [0.16, 1, 0.3, 1], 
                  delay: isLoadingComplete ? 0.8 : 0 
                }}
                viewport={{ once: true, margin: '-50px' }}
                whileHover={{ scale: 1.02 }}
                onClick={() => onNavigate('ocean')}
              >
                <div className="aspect-[1.72/1] w-full relative overflow-hidden rounded-sm">
                  <div 
                    className="absolute inset-0"
                    style={{
                      backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                      opacity: 0.1
                    }}
                  />
                  <video
                    className="w-full h-full object-cover"
                    src="/assets/videos/ocean-video.mp4"
                    autoPlay  
                    loop
                    muted
                    playsInline
                    style={{
                      filter: isDark ? 'brightness(0.95)' : 'brightness(1.05)'
                    }}
                  />
                  <div 
                    className="absolute inset-0 transition-all duration-300" 
                    style={{
                      backgroundColor: isDark ? 'rgba(0,0,0,0)' : 'rgba(255,255,255,0)'
                    }}
                  />
                </div>
                
                {/* Project Info */}
                <div className="flex items-start justify-start w-full pt-3 md:pt-4 overflow-hidden">
                  <span 
                    className="font-normal tracking-[-0.01em] uppercase font-mono transition-colors duration-300"
                    style={{
                      color: colors.text.primary,
                      fontSize: 'clamp(10px, 2.2vw, 13px)'
                    }}
                  >1</span>
                  <span 
                    className="font-normal tracking-[-0.01em] uppercase font-mono mx-2 transition-colors duration-300"
                    style={{
                      color: colors.text.primary,
                      fontSize: 'clamp(10px, 2.2vw, 13px)'
                    }}
                  >/</span>
                  <div className="flex-1 relative h-[14px] sm:h-[15px] md:h-[16px] lg:h-[17px] overflow-hidden">
                    <motion.div 
                      className="absolute top-0 left-0 font-normal tracking-[-0.01em] uppercase font-mono leading-none transition-colors duration-300"
                      style={{
                        color: colors.text.primary,
                        fontSize: 'clamp(10px, 2.2vw, 13px)'
                      }}
                      initial={{ opacity: 1, y: 0 }}
                      whileHover={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                    >
                      The Ocean
                    </motion.div>
                    <motion.div 
                      className="absolute top-0 left-0 font-normal tracking-[-0.01em] uppercase font-mono leading-none transition-colors duration-300"
                      style={{
                        color: colors.text.primary,
                        fontSize: 'clamp(10px, 2.2vw, 13px)'
                      }}
                      initial={{ opacity: 0, y: 20 }}
                      whileHover={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 }}
                    >
                      VIEW PROJECT
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Two Smaller Projects */}
            <div className="flex-1 space-y-6 sm:space-y-8 md:space-y-10 lg:space-y-12">
              {/* Project 2 - Mphepo */}
              <motion.div
                className="group relative cursor-pointer"
                initial={getInitialState({ opacity: 0, y: 140 })}
                whileInView={getAnimateState({ opacity: 1, y: 0 })}
                transition={{ 
                  duration: 2, 
                  ease: [0.16, 1, 0.3, 1], 
                  delay: isLoadingComplete ? 0.9 : 0 
                }}
                viewport={{ once: true, margin: '-50px' }}
                whileHover={{ scale: 1.02 }}
                onClick={() => onNavigate('mphepo')}
              >
                <div className="aspect-[1.27/1] w-full relative overflow-hidden rounded-sm">
                  <div 
                    className="absolute inset-0"
                    style={{
                      backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                      opacity: 0.1
                    }}
                  />
                  <img
                    className="w-full h-full object-cover transition-all duration-300 group-hover:scale-[1.02]"
                    src="/assets/images/tree.png"
                    alt="Beyond the Frame"
                    style={{
                      filter: isDark ? 'brightness(0.95)' : 'brightness(1.05)'
                    }}
                  />
                </div>
                
                <div className="flex items-start justify-start w-full pt-3 md:pt-4 overflow-hidden">
                  <span 
                    className="font-normal tracking-[-0.01em] uppercase font-mono transition-colors duration-300"
                    style={{
                      color: colors.text.primary,
                      fontSize: 'clamp(10px, 2.2vw, 13px)'
                    }}
                  >2</span>
                  <span 
                    className="font-normal tracking-[-0.01em] uppercase font-mono mx-2 transition-colors duration-300"
                    style={{
                      color: colors.text.primary,
                      fontSize: 'clamp(10px, 2.2vw, 13px)'
                    }}
                  >/</span>
                  <div className="flex-1 relative h-[14px] sm:h-[15px] md:h-[16px] lg:h-[17px] overflow-hidden">
                    <motion.div 
                      className="absolute top-0 left-0 font-normal tracking-[-0.01em] uppercase font-mono leading-none transition-colors duration-300"
                      style={{
                        color: colors.text.primary,
                        fontSize: 'clamp(10px, 2.2vw, 13px)'
                      }}
                      initial={{ opacity: 1, y: 0 }}
                      whileHover={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                    >
                      Mphepo
                    </motion.div>
                    <motion.div 
                      className="absolute top-0 left-0 font-normal tracking-[-0.01em] uppercase font-mono leading-none transition-colors duration-300"
                      style={{
                        color: colors.text.primary,
                        fontSize: 'clamp(10px, 2.2vw, 13px)'
                      }}
                      initial={{ opacity: 0, y: 20 }}
                      whileHover={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 }}
                    >
                      VIEW PROJECT
                    </motion.div>
                  </div>
                </div>
              </motion.div>

              {/* Project 3 - Poetry in Motion */}
              <motion.div
                className="group relative cursor-pointer"
                initial={getInitialState({ opacity: 0, y: 140 })}
                whileInView={getAnimateState({ opacity: 1, y: 0 })}
                transition={{ 
                  duration: 2, 
                  ease: [0.16, 1, 0.3, 1], 
                  delay: isLoadingComplete ? 1.0 : 0 
                }}
                viewport={{ once: true, margin: '-50px' }}
                whileHover={{ scale: 1.02 }}
                onClick={() => onNavigate('poetry-in-motion')}
              >
                <div className="aspect-[1.27/1] w-full relative overflow-hidden rounded-sm">
                  <div 
                    className="absolute inset-0"
                    style={{
                      backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                      opacity: 0.1
                    }}
                  />
                  <img
                    className="w-full h-full object-cover transition-all duration-300 group-hover:scale-[1.02]"
                    src="/assets/images/solokitchen.png"
                    alt="The Essence"
                    style={{
                      filter: isDark ? 'brightness(0.95)' : 'brightness(1.05)'
                    }}
                  />
                </div>
                
                <div className="flex items-start justify-start w-full pt-3 md:pt-4 overflow-hidden">
                  <span 
                    className="font-normal tracking-[-0.01em] uppercase font-mono transition-colors duration-300"
                    style={{
                      color: colors.text.primary,
                      fontSize: 'clamp(10px, 2.2vw, 13px)'
                    }}
                  >3</span>
                  <span 
                    className="font-normal tracking-[-0.01em] uppercase font-mono mx-2 transition-colors duration-300"
                    style={{
                      color: colors.text.primary,
                      fontSize: 'clamp(10px, 2.2vw, 13px)'
                    }}
                  >/</span>
                  <div className="flex-1 relative h-[14px] sm:h-[15px] md:h-[16px] lg:h-[17px] overflow-hidden">
                    <motion.div 
                      className="absolute top-0 left-0 font-normal tracking-[-0.01em] uppercase font-mono leading-none transition-colors duration-300"
                      style={{
                        color: colors.text.primary,
                        fontSize: 'clamp(10px, 2.2vw, 13px)'
                      }}
                      initial={{ opacity: 1, y: 0 }}
                      whileHover={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                    >
                      Poetry In Motion
                    </motion.div>
                    <motion.div 
                      className="absolute top-0 left-0 font-normal tracking-[-0.01em] uppercase font-mono leading-none transition-colors duration-300"
                      style={{
                        color: colors.text.primary,
                        fontSize: 'clamp(10px, 2.2vw, 13px)'
                      }}
                      initial={{ opacity: 0, y: 20 }}
                      whileHover={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 }}
                    >
                      VIEW PROJECT
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* View All Button */}
        <div className="flex items-center justify-center w-full pt-8 sm:pt-10 md:pt-12 lg:pt-14">
          <motion.button
            whileInView={getAnimateState({ opacity: 1 })}
            transition={{ 
              duration: 0.6, 
              ease: [0, 0, 1, 1], 
              delay: isLoadingComplete ? 0.5 : 0 
            }}
            viewport={{ once: true, margin: '-100px' }}
            onClick={() => onNavigate('writing')}
            className="font-normal tracking-[-0.01em] uppercase font-mono hover:opacity-70 transition-all duration-300 cursor-pointer"
            style={{
              color: colors.text.primary,
              fontSize: 'clamp(10px, 2.2vw, 13px)'
            }}
          >
            VIEW ALL
          </motion.button>
        </div>
      </section>

      {/* About Section */}
      <section 
        className="flex flex-col items-center justify-center min-h-screen py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12 relative transition-all duration-700"
        style={{ 
          backgroundColor: colors.primary,
          position: 'relative',
          zIndex: 2,
          borderBottomLeftRadius: 'clamp(40px, 8vw, 120px)',
          borderBottomRightRadius: 'clamp(40px, 8vw, 120px)',
          paddingBottom: 'clamp(60px, 10vw, 120px)',
          boxShadow: isDark 
            ? '0 20px 60px rgba(0, 0, 0, 0.4), 0 8px 20px rgba(0, 0, 0, 0.3)' 
            : '0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 20px rgba(0, 0, 0, 0.1)'
        }}
      >
        {/* Section Header */}
        <div className="flex flex-col items-start w-full max-w-[1200px] mb-12 sm:mb-16 md:mb-20 lg:mb-24">
          {/* Divider line */}
          <motion.div 
            className="w-full h-px mb-6 sm:mb-8 md:mb-10 lg:mb-12"
            initial={getInitialState({ scaleX: 0 })}
            whileInView={getAnimateState({ scaleX: 1 })}
            transition={{ 
              duration: 1.2, 
              ease: [0.16, 1, 0.3, 1] 
            }}
            viewport={{ once: true, margin: '-100px' }}
            style={{ transformOrigin: 'left' }}
          >
            <div 
              className="w-full h-px"
              style={{
                background: isDark 
                  ? 'linear-gradient(to right, #333333, #1a1a1a, transparent)' 
                  : 'linear-gradient(to right, #d4d1c9, #eae7de, transparent)'
              }}
            />
          </motion.div>

          {/* Title */}
          <div className="flex flex-col gap-1 sm:gap-2 mb-2 sm:mb-3 md:mb-4">
            <motion.div
              initial={getInitialState({ opacity: 0, y: 40 })}
              whileInView={getAnimateState({ opacity: 1, y: 0 })}
              transition={{ 
                duration: 1.2, 
                ease: [0.16, 1, 0.3, 1] 
              }}
              viewport={{ once: true, margin: '-100px' }}
              className="font-normal tracking-[-0.04em] leading-[0.85] transition-colors duration-500"
              style={{
                fontSize: 'clamp(32px, 8.5vw, 80px)',
                fontWeight: 400,
                letterSpacing: '-0.03em',
                color: colors.text.primary
              }}
            >
              ABOUT
            </motion.div>
            
            {/* Subtitle */}
            <motion.div
              initial={getInitialState({ opacity: 0, y: 30 })}
              whileInView={getAnimateState({ opacity: 1, y: 0 })}
              transition={{ 
                duration: 1, 
                ease: [0.16, 1, 0.3, 1], 
                delay: isLoadingComplete ? 0.2 : 0 
              }}
              viewport={{ once: true, margin: '-100px' }}
              className="font-light tracking-[0.02em] uppercase font-mono transition-colors duration-500"
              style={{
                fontSize: 'clamp(8px, 2vw, 12px)',
                letterSpacing: '0.1em',
                color: colors.text.accent
              }}
            >
              Sir Practice Jr.
            </motion.div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="w-full max-w-[1200px]">
          {/* Mobile Layout */}
          <div className="lg:hidden flex flex-col gap-6 sm:gap-8">
            {/* Mobile Image */}
            <motion.div
              initial={getInitialState({ opacity: 0, scale: 0.95 })}
              whileInView={getAnimateState({ opacity: 1, scale: 1 })}
              transition={{ 
                duration: 1.4, 
                ease: [0.16, 1, 0.3, 1], 
                delay: isLoadingComplete ? 0.3 : 0 
              }}
              viewport={{ once: true, margin: '-100px' }}
              className="relative group w-full flex justify-center"
            >
              <div className="relative overflow-hidden rounded-sm w-full max-w-[280px] sm:max-w-[320px]">
                <div className="aspect-[3/4] w-full relative">
                  <img
                    className="w-full h-full object-cover transition-all duration-700 group-hover:scale-[1.02]"
                    src="/assets/images/portfolio.jpeg"
                    alt="Sir Practice - Artist Portrait"
                    style={{
                      filter: isDark ? 'brightness(0.95)' : 'brightness(1.05)'
                    }}
                  />
                  <div 
                    className="absolute inset-0 z-10" 
                    style={{
                      background: isDark 
                        ? 'linear-gradient(to bottom right, transparent, transparent, rgba(0,0,0,0.2))' 
                        : 'linear-gradient(to bottom right, transparent, transparent, rgba(255,255,255,0.2))'
                    }}
                  />
                  <div 
                    className="absolute inset-0 border rounded-sm"
                    style={{
                      borderColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
                    }}
                  />
                </div>
              </div>
            </motion.div>
            
            {/* Mobile Content */}
            <div className="flex flex-col space-y-4 sm:space-y-5">
              {/* Primary Description */}
              <motion.div
                initial={getInitialState({ opacity: 0, y: 40 })}
                whileInView={getAnimateState({ opacity: 1, y: 0 })}
                transition={{ 
                  duration: 1.2, 
                  ease: [0.16, 1, 0.3, 1], 
                  delay: isLoadingComplete ? 0.4 : 0 
                }}
                viewport={{ once: true, margin: '-100px' }}
                className="font-light leading-[1.5] tracking-[-0.01em] text-center sm:text-left transition-colors duration-500"
                style={{
                  fontSize: 'clamp(16px, 4.2vw, 20px)',
                  color: colors.text.primary
                }}
              >
                Storyteller. Artist. Cartographer of identity.
                <br />
                <span style={{ color: colors.text.secondary }}>
                  Sir Practice traces the routes between heritage and becoming, pain and purpose.
                </span>
              </motion.div>
              
              {/* Secondary Description */}
              <motion.div
                initial={getInitialState({ opacity: 0, y: 40 })}
                whileInView={getAnimateState({ opacity: 1, y: 0 })}
                transition={{ 
                  duration: 1.2, 
                  ease: [0.16, 1, 0.3, 1], 
                  delay: isLoadingComplete ? 0.6 : 0 
                }}
                viewport={{ once: true, margin: '-100px' }}
                className="font-light leading-[1.6] tracking-[-0.005em] text-center sm:text-left transition-colors duration-500"
                style={{
                  fontSize: 'clamp(14px, 3.5vw, 17px)',
                  color: colors.text.muted
                }}
              >
                He believes that growth is a cycle of inhaling hope and exhaling purpose, and his work serves as both compass and companion for that journey.
              </motion.div>

              {/* Mobile CTA Button */}
              <motion.div
                initial={getInitialState({ opacity: 0, y: 20 })}
                whileInView={getAnimateState({ opacity: 1, y: 0 })}
                transition={{ 
                  duration: 0.8, 
                  ease: [0.16, 1, 0.3, 1], 
                  delay: isLoadingComplete ? 1 : 0 
                }}
                viewport={{ once: true, margin: '-100px' }}
                className="pt-4 sm:pt-5 flex justify-center sm:justify-start"
              >
                <button
                  onClick={() => onNavigate('about')}
                  className="group relative overflow-hidden"
                >
                  <div 
                    className="absolute inset-0 rounded-sm transition-all duration-300" 
                    style={{
                      backgroundColor: colors.secondary,
                      borderColor: colors.border,
                      border: `1px solid ${colors.border}`
                    }}
                  />
                  <div 
                    className="absolute inset-0 rounded-sm transition-all duration-300 opacity-0 group-hover:opacity-100"
                    style={{
                      backgroundColor: colors.hover,
                      borderColor: isDark ? '#555555' : '#c4c1b9'
                    }}
                  />
                  <div className="relative px-5 sm:px-6 py-3 sm:py-3 flex items-center gap-2 sm:gap-3">
                    <span 
                      className="font-normal tracking-[0.05em] uppercase font-mono transition-all duration-300 group-hover:tracking-[0.08em]"
                      style={{
                        fontSize: 'clamp(10px, 2.4vw, 12px)',
                        color: colors.text.primary
                      }}
                    >
                      Read More
                    </span>
                    <div className="w-3 h-3 sm:w-4 sm:h-4 relative overflow-hidden">
                      <div className="absolute inset-0 transform transition-transform duration-300 group-hover:translate-x-1">
                        <svg 
                          width="100%" 
                          height="100%" 
                          viewBox="0 0 16 16" 
                          fill="none" 
                          style={{ color: colors.text.primary }}
                        >
                          <path 
                            d="M6 4L10 8L6 12" 
                            stroke="currentColor" 
                            strokeWidth="1" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </button>
              </motion.div>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:grid grid-cols-12 gap-12 xl:gap-16 items-start">
            {/* Desktop Image Column */}
            <div className="col-span-5" style={{ position: 'relative', top: '-90px' }}>
              <motion.div
                initial={getInitialState({ opacity: 0, scale: 0.95 })}
                whileInView={getAnimateState({ opacity: 1, scale: 1 })}
                transition={{ 
                  duration: 1.4, 
                  ease: [0.16, 1, 0.3, 1], 
                  delay: isLoadingComplete ? 0.3 : 0 
                }}
                viewport={{ once: true, margin: '-100px' }}
                className="relative group"
              >
                <div className="relative overflow-hidden rounded-sm">
                  <div className="aspect-[4/6] w-full relative">
                    <img
                      className="w-full h-full object-cover transition-all duration-700 group-hover:scale-[1.02]"
                      src="/assets/images/portfolio.jpeg"
                      alt="Sir Practice - Artist Portrait"
                      style={{
                        filter: isDark ? 'brightness(0.95)' : 'brightness(1.05)'
                      }}
                    />
                    <div 
                      className="absolute inset-0 z-10" 
                      style={{
                        background: isDark 
                          ? 'linear-gradient(to bottom right, transparent, transparent, rgba(0,0,0,0.2))' 
                          : 'linear-gradient(to bottom right, transparent, transparent, rgba(255,255,255,0.2))'
                      }}
                    />
                    <div 
                      className="absolute inset-0 border rounded-sm"
                      style={{
                        borderColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
                      }}
                    />
                  </div>
                </div>
              </motion.div>
            </div>
            
            {/* Desktop Content Column */}
            <div className="col-span-7 flex flex-col justify-start space-y-10">
              {/* Primary Description */}
              <motion.div
                initial={getInitialState({ opacity: 0, y: 40 })}
                whileInView={getAnimateState({ opacity: 1, y: 0 })}
                transition={{ 
                  duration: 1.2, 
                  ease: [0.16, 1, 0.3, 1], 
                  delay: isLoadingComplete ? 0.4 : 0 
                }}
                viewport={{ once: true, margin: '-100px' }}
                className="font-light leading-[1.6] tracking-[-0.01em] transition-colors duration-500"
                style={{
                  fontSize: 'clamp(15px, 3.8vw, 22px)',
                  color: colors.text.primary
                }}
              >
                Storyteller. Artist. Cartographer of identity.
                <br />
                <span style={{ color: colors.text.secondary }}>
                  Sir Practice traces the routes between heritage and becoming, pain and purpose.
                </span>
              </motion.div>
              
              {/* Secondary Description */}
              <motion.div
                initial={getInitialState({ opacity: 0, y: 40 })}
                whileInView={getAnimateState({ opacity: 1, y: 0 })}
                transition={{ 
                  duration: 1.2, 
                  ease: [0.16, 1, 0.3, 1], 
                  delay: isLoadingComplete ? 0.6 : 0 
                }}
                viewport={{ once: true, margin: '-100px' }}
                className="font-light leading-[1.7] tracking-[-0.005em] transition-colors duration-500"
                style={{
                  fontSize: 'clamp(13px, 3.2vw, 18px)',
                  color: colors.text.muted
                }}
              >
                He believes that growth is a cycle of inhaling hope and exhaling purpose, and his work serves as both compass and companion for that journey.
              </motion.div>

              {/* Desktop CTA Button */}
              <motion.div
                initial={getInitialState({ opacity: 0, y: 20 })}
                whileInView={getAnimateState({ opacity: 1, y: 0 })}
                transition={{ 
                  duration: 0.8, 
                  ease: [0.16, 1, 0.3, 1], 
                  delay: isLoadingComplete ? 1 : 0 
                }}
                viewport={{ once: true, margin: '-100px' }}
                className="pt-6"
              >
                <button
                  onClick={() => onNavigate('about')}
                  className="group relative overflow-hidden"
                >
                  <div 
                    className="absolute inset-0 rounded-sm transition-all duration-300" 
                    style={{
                      backgroundColor: colors.secondary,
                      borderColor: colors.border,
                      border: `1px solid ${colors.border}`
                    }}
                  />
                  <div 
                    className="absolute inset-0 rounded-sm transition-all duration-300 opacity-0 group-hover:opacity-100"
                    style={{
                      backgroundColor: colors.hover,
                      borderColor: isDark ? '#555555' : '#c4c1b9'
                    }}
                  />
                  <div className="relative px-6 py-3 flex items-center gap-3">
                    <span 
                      className="font-normal tracking-[0.05em] uppercase font-mono transition-all duration-300 group-hover:tracking-[0.08em]"
                      style={{
                        fontSize: 'clamp(9px, 2.2vw, 12px)',
                        color: colors.text.primary
                      }}
                    >
                      Read More
                    </span>
                    <div className="w-4 h-4 relative overflow-hidden">
                      <div className="absolute inset-0 transform transition-transform duration-300 group-hover:translate-x-1">
                        <svg 
                          width="100%" 
                          height="100%" 
                          viewBox="0 0 16 16" 
                          fill="none" 
                          style={{ color: colors.text.primary }}
                        >
                          <path 
                            d="M6 4L10 8L6 12" 
                            stroke="currentColor" 
                            strokeWidth="1" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Image Section - Always Visible */}
      <section 
        className="w-full relative"
        style={{ 
          marginTop: 'clamp(-30px, -6vw, -80px)',
          zIndex: 0
        }}
      >
        <div className="relative w-full">
          {/* Footer Image */}
          <div className="w-full aspect-[21/9] relative">
            <img
              className="w-full h-full object-cover"
              src="/assets/images/footer.png"
              alt="Footer"
              style={{
                filter: isDark ? 'brightness(0.9) contrast(1.05)' : 'brightness(1.05) contrast(0.95)'
              }}
            />
            <div 
              className="absolute inset-0"
              style={{
                background: isDark 
                  ? 'radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.3) 100%)' 
                  : 'radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.1) 100%)'
              }}
            />
          </div>
        </div>
      </section>
      
      <style jsx="true">{`
        @import url('https://fonts.googleapis.com/css2?family=Azeret+Mono:ital,wght@0,400;0,700;1,400;1,700&display=swap');
        
        * {
          box-sizing: border-box;
        }
        
        body {
          margin: 0;
          padding: 0;
          font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif;
          background-color: #f6f3ec;
        }
        
        .font-mono {
          font-family: 'Azeret Mono', monospace;
        }

        @media (max-width: 640px) {
          body {
            overflow-x: hidden;
          }
          
          button {
            min-height: 44px;
            min-width: 44px;
          }
          
          section[style*="borderBottomLeftRadius"] {
            border-bottom-left-radius: 40px !important;
            border-bottom-right-radius: 40px !important;
          }
        }

        @media (min-width: 641px) and (max-width: 1024px) {
          .space-y-12 > * + * {
            margin-top: 2.5rem !important;
          }
        }

        @media (min-width: 1024px) {
          .lg\\:aspect-\\[4\\/6\\] {
            aspect-ratio: 4/6 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default LandingPage;